/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Constants } from 'app/core/models/constants';
import { AuthService } from 'app/core/security/auth/auth.service';
import { ApplicationTitleService } from 'app/layout/services/application-title.service';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiInfo } from 'app/shared/models/api-info.model';
import { ApplicationService } from 'app/shared/services/application.service';
import { CookieService } from 'app/shared/services/cookie.service';
import { SnackBarService } from 'app/shared/services/snack-bar.service';
import { RegexValidationStrings } from 'app/shared/validation/reg-ex-validation-constants';
import { environment } from 'environments/environment';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '../../../../../node_modules/@angular/forms';
import { AuthResponse } from '../api/auth-response.model';
import { ResetPasswordFormComponent } from '../forgot-password/components/reset-password-form.component';
import { ForgotPasswordService } from '../forgot-password/services/forgot-password-service';
import { OrganiserOnboardingService } from '../organiser-onboarding/services/organiser-onboarding-service';
import { OtpComponent } from '../otp/otp.component';
import { PermissionsService } from './../permissions/permissions.service';

// TODO move to shared
@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  @BlockUI('login') blockUI: NgBlockUI;
  @ViewChild('otpForm') otpForm: OtpComponent;
  @ViewChild('resetPasswordForm', { static: false }) resetPasswordForm: ResetPasswordFormComponent;
  isError: boolean;
  serverErrorMessage: string;
  loginInfo: string;
  appVersion = 'unknown build number';
  apiInfo: ApiInfo;
  dataForm: FormGroup;

  showOtpFormIfInformationLoaded: boolean = false;
  showCreateUserAccountForm: boolean = false;
  showLinkExpiredError: boolean = false; // KS0009380: Link expired
  showForgotPasswordForm: boolean = false;
  showResetPasswordForm: boolean = false;
  hideLoginForm: boolean = false; // normally showLoginForm is computed, but this can be set to forcefully hide it
  onboardingUserId: number;
  onboardEmailLinkToken: string = '';
  isOnboardingUser: boolean = false;
  onboardingOtpSent: boolean = false;
  forgotPasswordLinkToken: string = '';

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private appService: ApplicationService,
    private permissionsService: PermissionsService,
    private cookieService: CookieService,
    private formHelperService: ReactiveFormHelperService,
    private snackBar: SnackBarService,
    appTitleService: ApplicationTitleService,
    private vcr: ViewContainerRef,
    private organiserOnboardingService: OrganiserOnboardingService,
    private forgotPasswordService: ForgotPasswordService
  ) {
    this.dataForm = fb.group({
      username: ['', [Validators.required, Validators.maxLength(256), Validators.pattern(RegexValidationStrings.EMAIL)]],
      password: ['', []],
    });
    appTitleService.setTitle('Sign in');
  }

  ngOnInit() {
    this.appVersion = environment.buildLabel;

    this.appService.getBuildVersion().then((apiInfo: ApiInfo) => {
      this.apiInfo = apiInfo;
    });

    const reason = +this.route.snapshot.queryParams['r'];
    if (reason === 1) {
      this.loginInfo = 'Your session has expired or you are not authorised to complete the last action. Please sign in again';
    }

    this.onboardingUserId = this.route.snapshot.queryParams['organiser-onboard-id'];
    this.onboardEmailLinkToken = this.route.snapshot.queryParams['organiser-onboard-token'];
    this.forgotPasswordLinkToken = this.route.snapshot.queryParams['forgot-password-token'];
    if (this.onboardingUserId && this.onboardingUserId > 0) {
      // Start in onboarding mode (from email link created during sign-up process).
      this.switchToOnboardingUserMode(this.onboardingUserId);
      this.generateAndSendOnboardingOtp();
    } else if (this.forgotPasswordLinkToken && this.forgotPasswordLinkToken.length > 0) {
      // Start in forgot password mode (from email link created during forgot password process).
      //this.showForgotPasswordForm = true;
      //this.hideLoginForm = true;
      this.switchToForgotPasswordConfirmationMode();
    }
  }

  get username(): AbstractControl {
    return this.dataForm.controls['username'];
  }
  get password(): AbstractControl {
    return this.dataForm.controls['password'];
  }

  get showLoginForm(): boolean {
    return !(this.showOtpFormIfInformationLoaded || this.showLinkExpiredError || this.showForgotPasswordForm || this.hideLoginForm);
  }

  get showAppInfo(): boolean {
    return !this.showLinkExpiredError;
  }

  get showOtpForm(): boolean {
    return this.showOtpFormIfInformationLoaded && (this.onboardingOtpSent || !this.isOnboardingUser);
  }

  get usernameShouldUseMsAuthentication(): boolean{
    return this.username?.value?.toLowerCase()?.includes(environment.microsoftAuthenticationUseForUsernamesContaining) ?? false;
  }
  
  get usePasswordField(): boolean{
    return !this.usernameShouldUseMsAuthentication;
  }

  onSubmit(): void {
    this.onEmailAddressChanged();

    if (this.formHelperService.isFormValid(this.dataForm)) {
      if(this.usernameShouldUseMsAuthentication){
        this.signInViaMicrosoft(this.dataForm.controls['username'].value);
      }
      else{
        this.submitLoginRequest(this.dataForm.controls['username'].value, this.dataForm.controls['password'].value, null);
      }
    }
  }

  onEmailAddressChanged(): void {
    if(this.usePasswordField){
      this.password.setValidators([Validators.required]);
    }else{
      this.password.clearValidators();
    }
    this.password.updateValueAndValidity();
  }

  async signInViaMicrosoft(username: string) {
    console.log("signInViaMicrosoft()");
    this.blockUI.start();

    try{
      var authResponse = await this.auth.loginViaMicrosoft(username);
      if (authResponse.isError) {
        this.handleLoginError(authResponse);
      } else {
        this.reset();
        this.handleLoginSubmissionSuccess(authResponse);
      }
    }
    catch(err){
      console.error(err);
      this.isError = true;
      //this.setErrorMessage('An unknown error has occurred.');
      this.setErrorMessage(err);
    }
    finally{
      this.blockUI.stop();
    }
  }

  /* Standard username/password login */
  submitLoginRequest(username: string, password: string, otp: string) {
    this.blockUI.start();
    this.auth
      .login(username, password, otp)
      .then((response: AuthResponse) => {
        this.blockUI.stop();

        if (response.isError) {
          this.handleLoginError(response);
        } else {
          this.handleLoginSubmissionSuccess(response);
        }
      })
      .catch((error) => {
        this.blockUI.stop();
        this.isError = true;
        this.setErrorMessage('An unknown error has occurred.');
      });
  }

  completeUserOnboardingAndSignInWithOtp(otp: string) {
    // There's a requirement to complete onboarding and log in automatically here without either the username or password.
    // We only have the OTP, user ID and email token.
    this.blockUI.start();
    this.auth
      .completeOnboardingAndLogin(this.onboardingUserId, this.onboardEmailLinkToken, otp)
      .then((response: AuthResponse) => {
        this.blockUI.stop();

        if (response.isError) {
          this.handleLoginError(response);
        } else {
          this.handleLoginSubmissionSuccess(response);
        }
      })
      .catch((error) => {
        this.blockUI.stop();
        this.isError = true;
        this.setErrorMessage('An unknown error has occurred.');
      });
  }

  private handleLoginError(response: AuthResponse) {
    this.isError = response.isError;
    this.setErrorMessage(response.errorMessage);
    try {
      this.otpForm.reset();
    } catch {
      console.warn('Unable to reset OTP form.');
    }
    if (response.resetToFirstPageOfLogin) this.showOtpFormIfInformationLoaded = false;
  }

  private setErrorMessage(errorMessage: string) {
    this.serverErrorMessage = errorMessage;
  }

  private handleLoginSubmissionSuccess(response: AuthResponse) {
    if (response.data.requestOtp) {
      // UN/PW accepted, however an OTP is required to complete login
      this.showOtpFormIfInformationLoaded = true;
      // TODO: Attempt to find a way to focus on the OTP first digit by default (below method does not work)
      //this.otpForm.nativeElement.focus();
    } else {
      // No OTP required, proceed with final login success
      this.handleFinalLoginSuccess(response);
    }
  }

  private handleFinalLoginSuccess(response: AuthResponse) {
    this.reset();

    this.auth.loggedInUserId = +response.data.id;
    this.auth.loggedInUserName = response.data.username;
    this.auth.loggedInFullName = response.data.fullName;

    let redirectUrl = localStorage.getItem(Constants.LocalStorage.RedirectUrl);
    if (!redirectUrl || redirectUrl.length === 0 || redirectUrl.startsWith('/login') || redirectUrl.startsWith('login')) {
      // console.debug(`redirect to default redirect. redirectUrl = ${redirectUrl}`)
      redirectUrl = this.auth.getRedirectUrl();
    }

    console.debug(`redirect to default redirect. redirectUrl = ${redirectUrl}`);
    localStorage.setItem(Constants.LocalStorage.RedirectUrl, '');

    this.router.navigate([redirectUrl]);

    this.cookieService.setLoggedInUserId(this.auth.loggedInUserId);
    this.cookieService.setLoggedInUserName(this.auth.loggedInUserName);
    this.cookieService.setLoggedInFullName(this.auth.loggedInFullName);
    this.cookieService.setLoggedInUserType(response.data.userType);
    this.cookieService.setOrganiserId(response.data.organiserId);

    this.permissionsService.requestPermissionsAsync().then((results) => {});
  }

  private reset() {
    this.isError = false;
    this.serverErrorMessage = '';
    this.showOtpFormIfInformationLoaded = false;
    this.showLinkExpiredError = false;
    this.hideLoginForm = false;
  }

  forgotPassword(event) {
    event.preventDefault();
    //this.snackBar.warning("This feature is not implemented yet (KS0009191). Please contact your system administrator for assistance.");
    this.showForgotPasswordForm = true;
  }

  createUserAccount(event) {
    event.preventDefault();
    this.showCreateUserAccountForm = true;
    // Nothing in UATs for hiding the UN/PW but it makes sense to. It takes up a lot of screen space and doesn't make sense for it to still be open.
    this.hideLoginForm = true;
  }

  onSubmitOtp(otp: string) {
    if (this.isOnboardingUser) {
      this.completeUserOnboardingAndSignInWithOtp(otp);
    } else {
      this.submitLoginRequest(this.dataForm.controls['username'].value, this.dataForm.controls['password'].value, otp);
    }
  }

  onResendOtp(event) {
    if (this.isOnboardingUser) {
      this.generateAndSendOnboardingOtp();
    } else {
      // Re-sent sign-in OTP.
      // We can generate and re-send a new OTP by simply re-submitting the UN/PW form.
      this.onSubmit();
    }
    this.otpForm.reset();
  }

  onInactiveOrganiserUserAccountCreated(onboardingUserId: number) {
    //this.switchToOnboardingUserMode(onboardingUserId);

    // Discussed 2025-06-03: This will no longer show the OTP form automatically but instead display a message asking the user to click
    // the link in their email to continue. At the time of writing UATs don't seem to have been updated yet.
    // KS0009425 covers the new requirements.

    this.hideLoginForm = true;
    this.showCreateUserAccountForm = false;
    this.isError = true;
    this.setErrorMessage('An Email has been sent - please check your Inbox to complete the Sign Up process');
  }

  switchToOnboardingUserMode(onboardingUserId: number) {
    this.isOnboardingUser = true;
    this.showCreateUserAccountForm = false;
    this.showOtpFormIfInformationLoaded = true;
    this.onboardingUserId = onboardingUserId;
  }

  async generateAndSendOnboardingOtp() {
    var result = await this.organiserOnboardingService.generateAndSendOnboardingOtp(this.onboardingUserId, this.onboardEmailLinkToken);
    var otpWasSent = result && result.otpSent;
    if (!otpWasSent) {
      // KS0009380: Link expired
      this.isError = false;
      this.showOtpFormIfInformationLoaded = false;
      this.showLinkExpiredError = true;
    }
    this.onboardingOtpSent = otpWasSent;
  }

  async switchToForgotPasswordConfirmationMode() {
    this.hideLoginForm = true;

    var expirationCheckResult = await this.forgotPasswordService.checkForgotPasswordLinkUsedBeforeAndMarkAsUsed(this.forgotPasswordLinkToken);
    var expired = expirationCheckResult && expirationCheckResult.showLinkExpiredError;
    if (expired) {
      this.showLinkExpiredError = true;
    } else {
      this.showResetPasswordForm = true;
      this.resetPasswordForm.emailLinkToken = this.forgotPasswordLinkToken;
    }
  }
}
